"use client";

import { QuizStats as StatsType } from "@/lib/daily/types";
import { Award, CheckCircle } from "lucide-react";

interface QuizStatsProps {
  readonly stats: StatsType;
}

export default function QuizStats({ stats }: QuizStatsProps) {
  const categoriesMap: Record<string, string> = {
    tax: "Tax Planning",
    investing: "Investing",
    loans: "Loans & Debt",
    savings: "Savings",
    retirement: "Retirement",
    budgeting: "Budgeting",
    general: "General Finance",
  };

  // Filter out categories with 0 attempted questions (not in the quiz)
  const relevantCategories = Object.entries(stats.categoryPerformance).filter(([category, value]) => {
    // Check if this category had any questions attempted
    const cat = category as keyof typeof stats.categoryPerformance;
    return value > 0 || stats.categoryPerformance[cat] !== undefined;
  });

  // Further filter: only show categories that have actual data (not showing 0% for never-attempted categories)
  const displayCategories = relevantCategories.filter(([category, value]) => {
    // Get the category key to check in categoryCorrect and categoryTotal
    const correctKey = `${category}Correct` as keyof StatsType;
    const totalKey = `${category}Total` as keyof StatsType;
    
    // Only show if there were questions from this category
    return (stats[totalKey] as number) > 0;
  });

  return (
    <div style={{ width: "100%", marginTop: "0.75rem" }}>
      {/* Metrics Row */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: "0.5rem",
          margin: "0.75rem 0",
          padding: "0.5rem",
          backgroundColor: "var(--overlay-a02)",
          borderRadius: "8px",
          border: "1px solid var(--border-color)",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: "var(--text-xl)", fontWeight: "var(--fw-extrabold)" }}>{stats.quizzesTaken}</div>
          <div style={{ fontSize: "var(--text-2xs)", color: "var(--text-secondary)", fontWeight: "var(--fw-bold)", textTransform: "uppercase" }}>Quizzes</div>
        </div>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: "var(--text-xl)", fontWeight: "var(--fw-extrabold)" }}>{stats.averageScore}%</div>
          <div style={{ fontSize: "var(--text-2xs)", color: "var(--text-secondary)", fontWeight: "var(--fw-bold)", textTransform: "uppercase" }}>Avg Score</div>
        </div>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: "var(--text-xl)", fontWeight: "var(--fw-extrabold)" }}>{stats.maxStreak}</div>
          <div style={{ fontSize: "var(--text-2xs)", color: "var(--text-secondary)", fontWeight: "var(--fw-bold)", textTransform: "uppercase" }}>Max Streak</div>
        </div>
      </div>

      {/* Category Performance Breakdown - Only show categories present in quiz */}
      {displayCategories.length > 0 && (
        <>
          <h4
            style={{
              fontSize: "var(--text-xs)",
              fontWeight: "var(--fw-extrabold)",
              textTransform: "uppercase",
              color: "var(--text-muted)",
              letterSpacing: "0.05em",
              textAlign: "left",
              marginBottom: "0.5rem",
              marginTop: "0.75rem",
            }}
          >
            Category Performance
          </h4>
          <div style={{ display: "flex", flexDirection: "column", gap: "6px", textAlign: "left" }}>
            {displayCategories.map(([category, value]) => {
              const categoryName = categoriesMap[category] || category;
              const displayValue = Math.round(value);

              return (
                <div key={category} style={{ display: "flex", flexDirection: "column", gap: "3px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: "var(--text-xs)", fontWeight: "var(--fw-semibold)" }}>
                    <span>{categoryName}</span>
                    <span style={{ color: displayValue >= 75 ? "var(--primary)" : "var(--text-secondary)" }}>
                      {displayValue}%
                    </span>
                  </div>
                  <div style={{ width: "100%", height: "5px", backgroundColor: "var(--overlay-a03)", borderRadius: "3px", overflow: "hidden" }}>
                    <div
                      style={{
                        width: `${displayValue}%`,
                        height: "100%",
                        backgroundColor: displayValue >= 75 ? "var(--primary)" : "var(--text-muted)",
                        borderRadius: "3px",
                        transition: "width 0.5s ease-out",
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
